import { ICellRendererParams } from "ag-grid-community";
import { forwardRef, ReactElement, useRef } from "react";
import { Box, Tooltip } from "@chakra-ui/react";
import { typedSxMap } from "../../../utils/typedSxMap";
import { FaCircleCheck, FaRegClock } from "react-icons/fa6";
import { MdError } from "react-icons/md";
import { PiInfo } from "react-icons/pi";

type IconKeyType = "error" | "warning" | "info";

const iconTypeMap: Record<IconKeyType, ReactElement> = {
  error: <PiInfo />,
  warning: <PiInfo />,
  info: <PiInfo />,
};

export const CELL_COLOR_MAP = {
  error: "var(--color-cell-background-error)",
  warning: "var(--color-cell-background-warning)",
  info: "var(--color-cell-background-error)",
} as const;

export type Severity = "error" | "warning" | "info";

const styles = typedSxMap({
  container: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    height: "calc(100% - 1px)",
    alignItems: "center",
    gap: "var(--m-xs)",
    "& > span": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  errors: {
    display: "flex",
  },
  tooltipWrapper: {
    position: "absolute",
    transform: "translateX(-50%)",
    backgroundColor: "var(--color-background-modal)",
    padding: "var(--m-xxs) var(--m-xs)",
    borderRadius: "var(--border-radius)",
    marginTop: "var(--m-xs)",
    boxShadow: "0 0 0 1px var(--color-border), 0 5px 15px rgba(0, 0, 0, 0.2)",
    maxWidth: "300px",
    zIndex: 1000,
    color: "var(--color-text)",
  },
});

const getIconType = (type: string): ReactElement => {
  return iconTypeMap[type as IconKeyType] || <PiInfo />;
};

type CellRendererProps = ICellRendererParams & {
  setTooltip?: (value: any, shouldShow: () => boolean) => void;
};

export const DefaultCellRenderer = forwardRef<HTMLDivElement, CellRendererProps>((props, ref) => {
  const field = props.column?.getColId();
  const errors = field ? props.data?.errors?.[field] : undefined;

  return (
    <Box ref={ref} sx={styles.container}>
      <span>{props.value}</span>
      {errors && (
        <Box sx={styles.errors}>
          <Tooltip
            sx={styles.tooltipWrapper}
            label={errors[0].message}
            aria-label={errors[0].message}
            arrowShadowColor="var(--color-border)"
            placement="top">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "var(--m-xs)",
                paddingLeft: "var(--m-xxs)",
                verticalAlign: "middle",
              }}>
              {getIconType(errors[0].type)}
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
});

export const StatusCellRenderer = forwardRef<HTMLDivElement, CellRendererProps>((props, ref) => {
  const wrapper = useRef<HTMLDivElement>(null);
  const status = props.value?.toLowerCase();
  const iconStyle = { marginRight: "6px" };

  // useEffect(() => {
  //   if (!wrapper.current) return;
  //   props.setTooltip?.(props.value, () => wrapper.current!.scrollWidth > wrapper.current!.clientWidth);
  // }, [wrapper]);

  const renderContent = () => {
    switch (status) {
      case "accepted":
        return <FaCircleCheck color="green" size={13.33} style={iconStyle} />;
      case "rejected":
        return <MdError color="var(--color-cell-error)" size={16} style={{ marginRight: "6px", marginLeft: "-1px" }} />;
      case "pending":
        return <FaRegClock color="var(--color-text-soft)" size={13.33} style={iconStyle} />;
      default:
        return null;
    }
  };

  return (
    <div ref={wrapper} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {renderContent()}
        {props.value}
      </div>
    </div>
  );
});
